﻿import { Checkbox, NumericTextBox } from '@progress/kendo-react-inputs';
import SettingsSubSection from '../../../../../components/common/SettingsSubSection';
import { UDViewPlaneIconSizeEnum, UDViewPlaneIconTypeEnum, UDViewRangeType } from '../../../../../udView/udview';
import { ColorPickerWithDefault } from '../../../../components/inputs/ColorPickerWithDefault';
import { DropdownListWithValueField } from '../../../../components/inputs/DropdownListWithValueField';
import ZoomLevelSlider from '../../../../components/inputs/ZoomLevelSlider';
import useView from '../../hooks/use-view';
import {
    useViewCheckboxValue,
    useViewColorValue,
    useViewDropdownValue,
    useViewNumericValue,
    useViewSliderValue
} from '../../hooks/view-forms';

const iconSizeOptions = [
    { value: UDViewPlaneIconSizeEnum.auto, text: 'Auto' },
    { value: UDViewPlaneIconSizeEnum.small, text: 'Small' },
    { value: UDViewPlaneIconSizeEnum.medium, text: 'Medium' },
    { value: UDViewPlaneIconSizeEnum.large, text: 'Large' },
    { value: UDViewPlaneIconSizeEnum.extraLarge, text: 'Extra Large' },
    { value: UDViewPlaneIconSizeEnum.jumbo, text: 'Jumbo' }
];

const iconTypeOptions = [
    { value: UDViewPlaneIconTypeEnum.auto, text: 'Auto' },
    { value: UDViewPlaneIconTypeEnum.circle, text: 'Circle' },
    { value: UDViewPlaneIconTypeEnum.triangle, text: 'Triangle' }
];

const ringsUnitOptions = [
    { value: UDViewRangeType.time, text: 'Minutes (@ curr. speed)' },
    { value: UDViewRangeType.time389, text: 'Minutes (@ 389 kts)' },
    { value: UDViewRangeType.timeCustomSpeed, text: 'Minutes (@ custom speed)' },
    { value: UDViewRangeType.distance, text: 'Miles (fixed)' },
    { value: UDViewRangeType.distanceRLOS, text: 'Miles (RLOS @ curr. alt.)' },
    { value: UDViewRangeType.pixel, text: 'Pixels' },
    { value: UDViewRangeType.distanceFobCurrentSpeed, text: 'Range (FOB @ curr. speed)' },
    { value: UDViewRangeType.distanceFobCustomSpeed, text: 'Range (FOB @ custom speed)' }
];
const velocityVectorUnitOptions = [
    { value: UDViewRangeType.time, text: 'Minutes (@ curr. speed)' },
    { value: UDViewRangeType.distance, text: 'Miles (fixed)' }
];

const RINGS_FILL_DEFAULT_COLOR = '#30d4fc55';
const RINGS_LINE_DEFAULT_COLOR = '#12cafc';
const VECTOR_LINE_DEFAULT_COLOR = '#12cafc';

export default function IconRingsSettings() {
    const drawIconShadow = useViewCheckboxValue((v) => v.plane.icon.drawShadow);
    const iconZoomLevel = useViewSliderValue((v) => v.plane.icon.zoomLevel);
    const iconSize = useViewDropdownValue(iconSizeOptions, (v) => v.plane.icon.size);
    const iconType = useViewDropdownValue(iconTypeOptions, (v) => v.plane.icon.type);
    const { view } = useView();
    const drawRings = useViewCheckboxValue((v) => v.plane.range.isVisible);
    const ringsRange = useViewNumericValue((v) => v.plane.range.factor);
    const ringsUnit = useViewDropdownValue(ringsUnitOptions, (v) => v.plane.range.type);
    const ringsFillColor = useViewColorValue((v) => v.plane.range.color ?? RINGS_FILL_DEFAULT_COLOR);
    const ringsLineColor = useViewColorValue((v) => v.plane.range.lineColor ?? RINGS_LINE_DEFAULT_COLOR);

    const drawVelocityVectors = useViewCheckboxValue((v) => v.plane.vector.isVisible);
    const vectorMagnitude = useViewNumericValue((v) => v.plane.vector.factor);
    const vectorUnit = useViewDropdownValue(velocityVectorUnitOptions, (v) => v.plane.vector.type);
    const vectorLineColor = useViewColorValue((v) => v.plane.vector.lineColor ?? VECTOR_LINE_DEFAULT_COLOR);

    const vectorUnitFormat = () => {
        switch (vectorUnit.value) {
            case UDViewRangeType.distance:
                return '###.# Nm';
            case UDViewRangeType.time:
            case UDViewRangeType.time389:
            case UDViewRangeType.timeCustomSpeed:
                return '###.# min';
            case UDViewRangeType.pixel:
                return '### px';
            default:
                return 'n2';
        }
    };
    const ringsUnitFormat = () => {
        switch (ringsUnit.value) {
            case UDViewRangeType.distance:
                return '###.# Nm';
            case UDViewRangeType.time:
            case UDViewRangeType.time389:
            case UDViewRangeType.timeCustomSpeed:
                return '###.# min';
            case UDViewRangeType.pixel:
                return '### px';
            default:
                return 'n2';
        }
    };
    const isRingsValueInputDisabled = () => {
        return (
            ringsUnit.value === UDViewRangeType.distanceFobCurrentSpeed ||
            ringsUnit.value === UDViewRangeType.distanceFobCustomSpeed ||
            ringsUnit.value === UDViewRangeType.distanceRLOS
        );
    };

    return (
        <div className="d-flex flex-column gap-2 px-3 pb-2">
            <SettingsSubSection title={'Icons'}>
                <Checkbox
                    {...drawIconShadow}
                    className="flex-grow-1"
                    label={'Draw icon shadow'}
                    disabled={view.map.useGlobeView}></Checkbox>
                <ZoomLevelSlider {...iconZoomLevel} className={'w-50'} />
                <div className="d-flex align-items-center justify-content-between gap-2 w-50">
                    <div className={'flex-shrink-0'}>Icon size:</div>
                    <DropdownListWithValueField {...iconSize} rounded={'large'}></DropdownListWithValueField>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2 w-50">
                    <div className={'flex-shrink-0'}>Icon type:</div>
                    <DropdownListWithValueField {...iconType} rounded={'large'}></DropdownListWithValueField>
                </div>
            </SettingsSubSection>

            <SettingsSubSection title={'Rings'}>
                <Checkbox {...drawRings} className="flex-grow-1" label={'Draw rings'}></Checkbox>
                <div style={{ gridTemplateColumns: '1fr 2fr' }} className="d-grid gap-2">
                    <span className={'secondary-text'}>Value:</span>
                    <span className={'secondary-text'}>Unit:</span>
                    <NumericTextBox
                        min={0}
                        {...ringsRange}
                        format={ringsUnitFormat()}
                        disabled={isRingsValueInputDisabled()}
                        rounded={'large'}></NumericTextBox>
                    <DropdownListWithValueField {...ringsUnit} rounded={'large'}></DropdownListWithValueField>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2" style={{ width: '33%' }}>
                    <div>Fill color:</div>
                    <ColorPickerWithDefault {...ringsFillColor} defaultColor={RINGS_FILL_DEFAULT_COLOR} />
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2" style={{ width: '33%' }}>
                    <div>Line color:</div>
                    <ColorPickerWithDefault {...ringsLineColor} defaultColor={RINGS_LINE_DEFAULT_COLOR} />
                </div>
            </SettingsSubSection>

            <SettingsSubSection title={'Velocity vectors'}>
                <Checkbox {...drawVelocityVectors} className="flex-grow-1" label={'Draw velocity vectors'}></Checkbox>
                <div style={{ gridTemplateColumns: '1fr 2fr' }} className="d-grid gap-2">
                    <span className={'secondary-text'}>Magnitude:</span>
                    <span className={'secondary-text'}>Unit:</span>
                    <NumericTextBox {...vectorMagnitude} format={vectorUnitFormat()} min={0} rounded={'large'}></NumericTextBox>
                    <DropdownListWithValueField {...vectorUnit} rounded={'large'}></DropdownListWithValueField>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2" style={{ width: '33%' }}>
                    <div>Line color:</div>
                    <ColorPickerWithDefault
                        {...vectorLineColor}
                        views={['gradient', 'palette']}
                        defaultColor={VECTOR_LINE_DEFAULT_COLOR}
                    />
                </div>
            </SettingsSubSection>
        </div>
    );
}
